import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { UserRound as CardIconUserRound } from 'lucide-react';
import { ClientCredit } from './client-credit.js';
import { ClientBalances } from '../modules/prepaid-balances/index.js';
import { formatPhone } from '@hue/contracts';
import { Images } from 'lucide-react';
import {
  Button,
  SegmentedControl,
  StatusPill,
  useSnackbar,
  type CivilDate,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clientsApi, getApiError } from '../lib/api.js';
import { formatDate } from '../lib/time-zone.js';
import { ClientConsents, ClientProfile } from '../modules/clients/index.js';
import { ClientRecords } from '../modules/records/index.js';
import { ClientAppointments } from '../modules/scheduling/index.js';
import { useCompanyScope } from './company-context.js';
import { ErrorCard, LoadingCard } from './data-view.js';
import { GatedCompanyScreen } from './gated-company-screen.js';
import type { ClientPageView } from './list-views.js';
import type { ViewSetter } from './view-state.js';
import { PageHeader } from './page-title.js';

/// A página do cliente (ADR 0030): o cadastro e os atendimentos dele, agendados e realizados, e o
/// Prontuário (ADR 0038). Vive na composição porque o cadastro é de Clientes, os atendimentos são
/// da Agenda e as fichas são do Prontuário; nenhum importa o outro. Sem a Agenda, a aba de
/// atendimentos não aparece e a página abre no cadastro; sem `records.read`, a aba Prontuário não
/// existe.
export function ClientPageScreen({
  clientId,
  view,
  onView,
  onBack,
  onOpenClosing,
  onOpenAgenda,
  onOpenClient,
  onOpenWithdrawal,
}: {
  onOpenWithdrawal?: ((balanceId: string) => void) | undefined;
  clientId: string;
  view: ClientPageView;
  onView: ViewSetter<ClientPageView>;
  onBack: () => void;
  onOpenClosing: (appointmentId: string) => void;
  onOpenAgenda: (date: CivilDate) => void;
  onOpenClient: (clientId: string) => void;
}) {
  const { can, timeZone, today, company } = useCompanyScope();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const client = useQuery({
    queryKey: ['company', 'clients', clientId],
    queryFn: () => clientsApi.get(clientId),
    enabled: can('clients.read'),
    retry: false,
  });
  const seesAppointments = can('scheduling.read');
  const seesRecords = can('records.read');
  const tabs = [
    ...(seesAppointments ? [['appointments', 'Atendimentos'] as const] : []),
    ['profile', 'Cadastro'] as const,
    ...(can('prepaid_balances.read') ? [['balances', 'Saldos pré-pagos'] as const] : []),
    ...(seesRecords ? [['records', 'Prontuário'] as const] : []),
  ];
  const tab = tabs.some(([value]) => value === view.tab) ? view.tab : 'profile';
  const data = client.data;

  return (
    <GatedCompanyScreen allowed={can('clients.read')}>
      {client.isLoading ? (
        <LoadingCard label="Carregando cliente" />
      ) : client.error || !data ? (
        <ErrorCard message={getApiError(client.error)} />
      ) : (
        <section aria-labelledby="client-page-title">
          <PageHeader
            onBack={onBack}
            trail="Cadastros / Clientes"
            id="client-page-title"
            title={data.displayName}
            actions={
              seesRecords ? (
                <Button
                  variant="outline"
                  onClick={() =>
                    onView({ tab: 'records', section: 'photos', photoAppointment: undefined })
                  }
                >
                  <Images className="size-4" /> Fotos e evolução
                </Button>
              ) : undefined
            }
          >
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-content-secondary">
              {data.active ? (
                <StatusPill tone="positive">Ativo</StatusPill>
              ) : (
                <StatusPill tone="neutral">Arquivado</StatusPill>
              )}
              {data.phone ? <span className="tabular-nums">{formatPhone(data.phone)}</span> : null}
              {data.email ? <span className="break-all">{data.email}</span> : null}
              {data.instagram ? (
                <a
                  href={`https://instagram.com/${data.instagram}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-brand-400 hover:underline"
                >
                  @{data.instagram}
                </a>
              ) : null}
              {data.referredBy ? (
                <span>
                  indicado por{' '}
                  <button
                    type="button"
                    onClick={() => onOpenClient(data.referredBy!.id)}
                    className="cursor-pointer font-semibold text-brand-400 hover:underline"
                  >
                    {data.referredBy.displayName}
                  </button>
                </span>
              ) : data.referralSource ? (
                <span>chegou por {data.referralSource}</span>
              ) : null}
              {data.referralsCount > 0 ? (
                <span>
                  indicou{' '}
                  <strong className="text-content">
                    {data.referralsCount} {data.referralsCount === 1 ? 'cliente' : 'clientes'}
                  </strong>
                </span>
              ) : null}
              <span>cliente desde {formatDate(data.createdAt, timeZone)}</span>
            </div>
          </PageHeader>
          <div className="grid grid-cols-[minmax(0,1fr)] gap-6">
            <ClientCredit clientId={clientId} />

            {tabs.length > 1 ? (
              <SegmentedControl
                tabs
                label="Seções do cliente"
                value={tab}
                onChange={(value) => onView({ tab: value })}
                options={tabs.map(([value, label]) => ({ value, label }))}
              />
            ) : null}

            {tab === 'balances' ? (
              <ClientBalances
                clientId={clientId}
                onOpenWithdrawal={
                  can('prepaid_balances.use') &&
                  can('inventory.read') &&
                  can('inventory.movements.create')
                    ? onOpenWithdrawal
                    : undefined
                }
                canWithdraw={
                  can('prepaid_balances.use') &&
                  can('inventory.read') &&
                  can('inventory.movements.create')
                }
                canUse={can('prepaid_balances.use')}
                canAdjust={can('prepaid_balances.adjust')}
                activeUnitId={company.activeBusinessUnitId ?? null}
              />
            ) : tab === 'records' ? (
              <ClientRecords
                clientId={clientId}
                timeZone={timeZone}
                today={today}
                view={{
                  section: view.section,
                  photoAppointment: view.photoAppointment,
                  record: view.record,
                  version: view.version,
                  compare: view.compare,
                  edit: view.edit,
                  timeline: view.timeline,
                  full: view.full,
                }}
                onView={onView}
                canManage={can('records.manage')}
                consents={
                  <ClientConsents
                    client={data}
                    canUpdate={can('clients.update')}
                    timeZone={timeZone}
                  />
                }
              />
            ) : tab === 'appointments' ? (
              <ClientAppointments
                clientId={clientId}
                timeZone={timeZone}
                view={view}
                onView={onView}
                onOpenClosing={onOpenClosing}
                onOpenAgenda={onOpenAgenda}
              />
            ) : (
              <div className="rounded-2xl border border-surface-border bg-surface-card p-5 shadow-soft">
                <ContentCardHeader
                  title="Cadastro do cliente"
                  icon={<CardIconUserRound className="size-4" />}
                />
                <ClientProfile
                  client={data}
                  capabilities={{
                    canCreate: can('clients.create'),
                    canUpdate: can('clients.update'),
                    canArchive: can('clients.delete'),
                    canManageAccess: can('clients.access.manage'),
                  }}
                  today={today}
                  timeZone={timeZone}
                  onOpenClient={onOpenClient}
                  onSaved={async () => {
                    await queryClient.invalidateQueries({ queryKey: ['company', 'clients'] });
                    snackbar.success('Cliente atualizado.');
                  }}
                />
              </div>
            )}
          </div>
        </section>
      )}
    </GatedCompanyScreen>
  );
}
